import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type PropsWithChildren,
} from "react";

import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { Spinner } from "../spinner";

export interface ButtonProps extends ComponentPropsWithoutRef<"button"> {
  loading?: boolean;
  size?: "sm" | "lg";
  variant:
    | "primary"
    | "secondary"
    | "tertiary"
    | "danger"
    | "alert"
    | "notice"
    | "quick-select";
}

export const Button = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<ButtonProps>
>(
  (
    { size = "lg", variant, disabled, loading, className, children, ...props },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        {...props}
        disabled={isDisabled}
        className={twMerge(
          "inline-flex items-center justify-center gap-2 font-semibold rounded-full text-xs",
          "focus:ring-2 focus:ring-v2-light align-middle",
          "active:shadow-[inset_4px_4px_4px_rgba(0,0,0,0.15)]",
          clsx({
            "h-8 px-3": size === "sm",
            "h-9 px-4": size === "lg",
            "text-white bg-v2-action-primary hover:bg-v2-action-hover":
              variant === "primary",
            "text-v2-action bg-v2-primary border border-v2-secondary":
              variant === "secondary",
            "hover:text-v2-action-hover hover:bg-v2-secondary hover:border-v2-light focus:text-v2-action-hover active:text-v2-action-hover":
              variant === "secondary",
            "hover:border-v2-light focus:border-v2-action-hover active:border-v2-action-hover":
              variant === "secondary",
            "hover:bg-v2-secondary focus:bg-v2-secondary active:bg-v2-secondary":
              variant === "secondary" || variant === "tertiary",
            "rounded text-v2-action hover:text-v2-action-hover hover:bg-v2-secondary":
              variant === "tertiary",
            "text-white bg-v2-action-negative hover:bg-v2-action-negative-hover":
              variant === "danger",
            "text-white bg-v2-action-call-out hover:bg-v2-action-call-out-hover":
              variant === "alert",
            "text-white bg-v2-action-positive hover:bg-v2-action-positive-hover":
              variant === "notice",
            "font-medium border border-v2-secondary rounded bg-v2-primary hover:bg-v2-secondary text-v2-primary":
              variant === "quick-select",
            "hover:border-v2-light focus:border-v2-action-primary active:border-v2-action-primary":
              variant === "quick-select",
            "focus:bg-v2-secondary focus:font-semibold active:bg-v2-secondary active:font-semibold":
              variant === "quick-select",
            "pointer-events-none text-v2-disabled bg-v2-action-disabled":
              isDisabled,
            "bg-v2-primary border-v2-action-disabled":
              isDisabled &&
              (variant === "secondary" || variant === "quick-select"),
            "bg-v2-primary": isDisabled && variant === "tertiary",
          }),
          className,
        )}
      >
        {loading && <Spinner />}
        {children}
      </button>
    );
  },
);
